import mainScheduler from "./scheduler";
import { Dialog } from "../ui/framework/dialog";
import TranslationService from "../services/translation";
import * as logger from "./logger";
import { ErrorHandler } from "./error.impl";
import { getServerDate } from "./storage/date";
import constants from "../constants";
import { htmlEscape } from "./vanilla";
import { DriveStore } from "../store/reducers";

/**
 * Units are all in milliseconds, matching with constants.ts
 */
interface IDriveConfiguration {
    syncInterval?: number;
    syncHosInterval?: number;
    systemSettingsInterval?: number;
    networkBridgeInterval?: number;
    driverChangeInterval?: number;
    clockDriftInterval?: number;
    dataTransferComplianceInterval?: number;
    tracedTextUploadInterval?: number;
    tracedTextUploadLimit?: number;
    isVerboseLoggingEnabled?: boolean;
    reset?: boolean;
}

export interface ICommand {
    name: string;
    data?: any;
}

export class CommandHandler {
    // Map command names to handler functions (only allow letters and hyphens in command names)
    private readonly commandToFunctionMap: Map<string, Function> = new Map<string, Function>([
        ["set-drive-configuration", this.setConfiguration.bind(this)],
        ["check-for-updates", this.checkForUpdates.bind(this)],
        ["emergency-notification", this.emergencyNotification.bind(this)],
        ["send-feedback-data", this.sendFeedbackData.bind(this)]
    ]);

    constructor(
        private readonly store: DriveStore,
        private readonly userName: string,
        private readonly errorHandler: ErrorHandler,
        private readonly translator: TranslationService
    ) {}

    handle(command: ICommand) {
        if (!command || !command.name) {
            logger.error(logger.Source.API, `Invalid command received`);
            return;
        }

        // Allow only letters and hyphens in command names
        if (!/^[a-zA-Z-]+$/.test(command.name)) {
            logger.error(logger.Source.API, `Invalid command name received: ${ JSON.stringify(command.name) }`);
            return;
        }

        let handler = this.commandToFunctionMap.get(command.name);
        if (!handler) {
            logger.error(logger.Source.API, `Handler not found for command: ${ command.name }`);
            return;
        }
        handler(command.data);
    }

    private checkForUpdates() {
        //TODO: ui/settings/main.checkForUpdates is not accessible. MYG-10347 was supposed to complete this but did not succeed. Need follow up case.
        throw new Error("Method not implemented");
    }

    private emergencyNotification(data: string) {
        if (!data) {
            throw new Error("No emergency notification provided");
        }
        void new Dialog(
            "emergency-notification",
            htmlEscape(data),
            this.translator.getTranslator(),
            "Important!",
            undefined,
            {
                isNightMode: this.store.getState().data.nightMode
            }
        ).open();
    }

    private setConfiguration(data?: IDriveConfiguration) {
        if (!data) {
            throw new Error("No data provided for configuration");
        }

        if (data.reset) {
            data.systemSettingsInterval = constants.SYSTEM_SETTINGS_INTERVAL;
            data.clockDriftInterval = constants.CLOCK_DRIFT_INTERVAL;
            data.syncInterval = constants.SYNC;
            data.syncHosInterval = constants.SYNC_HOS_INTERVAL;
            data.networkBridgeInterval = constants.BRIDGE.REFRESH_INTERVAL;
            data.driverChangeInterval = constants.DRIVER_CHANGE_INTERVAL;
            data.tracedTextUploadInterval = constants.RAPID_SYNC_EVENTS;
            data.isVerboseLoggingEnabled = false;
        }

        let mapTaskToInterval = {
            "@systemSettings": data.systemSettingsInterval,
            "@clockDrift": data.clockDriftInterval,
            "@sync": data.syncInterval,
            "@sync_hos": data.syncHosInterval,
            "@networkBridge": data.networkBridgeInterval,
            "@checkDriverChanges": data.driverChangeInterval,
            "@uploadEventLogs": data.tracedTextUploadInterval
        };

        for (let task in mapTaskToInterval) {
            mapTaskToInterval[task] && mainScheduler.setInterval(task, mapTaskToInterval[task], this.userName).fire(task, this.userName);
        }

        /**
         * Enable or disable verbose logging. It affects app immediately and is reset
         * when app is either restarted, or receives another second command with isEnabled=false
         */
        if (data.isVerboseLoggingEnabled !== undefined) {
            constants.LOGGING.VERBOSE_OVERRIDE = data.isVerboseLoggingEnabled;
        }
    }

    private async sendFeedbackData() {
        logger.feedback(`AUTOMATED-FEEDBACK-COMMAND-${ getServerDate().toString() }`, await this.errorHandler.getAdditionalDetails());
    }
}